import { useState, useEffect, useRef } from "react";
import "./style.css";

export default function VideoCallWindow({ 
  call, 
  currentUser,
  onEndCall, 
  socket 
}) {
  const [callDuration, setCallDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [peerConnection, setPeerConnection] = useState(null);
  const [isConnecting, setIsConnecting] = useState(true);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Debug logging for component lifecycle
  useEffect(() => {
    if (!call || !currentUser) return;
    
    console.log("📞 VideoCallWindow mounted:", { 
      callId: call?._id, 
      callStatus: call?.status,
      callType: call?.type,
      currentUserId: currentUser?.id
    });
    
    return () => {
      console.log("📞 VideoCallWindow unmounting:", { callId: call?._id });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?._id, call?.status, call?.type, currentUser?.id]);

  // Initialize WebRTC when component mounts
  useEffect(() => {
    if (call && socket) {
      initializeWebRTC();
    }

    return () => {
      // Cleanup streams and connections
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      if (peerConnection) {
        peerConnection.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call, socket]);

  // Handle incoming WebRTC offers
  useEffect(() => {
    if (!socket || !peerConnection || !call) {
      console.log("🔌 Socket or peerConnection or call not ready:", { 
        socket: !!socket, 
        peerConnection: !!peerConnection,
        call: !!call
      });
      return;
    }

    console.log("🔌 Setting up WebRTC event listeners for call:", call._id);

    const handleCallOffer = async ({ offer, callId, callerId }) => {
      console.log("📞 Received call offer:", { callId, callerId, currentCallId: call._id });
      if (callId === call._id) {
        console.log("📞 Processing call offer for current call");
        try {
          await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
          console.log("📞 Remote description set successfully");
          
          const answer = await peerConnection.createAnswer();
          console.log("📞 Created answer:", answer);
          
          await peerConnection.setLocalDescription(answer);
          console.log("📞 Local description set successfully");
          
          socket.emit('call-answer', {
            answer: answer,
            callId: call._id,
            targetUserId: callerId
          });
          console.log("📞 Sent call answer to:", callerId);
        } catch (error) {
          console.error("❌ Error handling call offer:", error);
        }
      } else {
        console.log("📞 Call ID mismatch:", { received: callId, current: call._id });
      }
    };

    const handleCallAnswer = async ({ answer, callId }) => {
      console.log("📞 Received call answer:", { callId, currentCallId: call._id });
      if (callId === call._id) {
        console.log("📞 Processing call answer for current call");
        try {
          await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
          console.log("📞 Remote description set from answer successfully");
        } catch (error) {
          console.error("❌ Error handling call answer:", error);
        }
      } else {
        console.log("📞 Call ID mismatch for answer:", { received: callId, current: call._id });
      }
    };

    const handleIceCandidate = async ({ candidate, callId }) => {
      console.log("🧊 Received ICE candidate:", { callId, currentCallId: call._id });
      if (callId === call._id) {
        console.log("🧊 Processing ICE candidate for current call");
        try {
          await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
          console.log("🧊 ICE candidate added successfully");
        } catch (error) {
          console.error("❌ Error handling ICE candidate:", error);
        }
      } else {
        console.log("🧊 Call ID mismatch for ICE candidate:", { received: callId, current: call._id });
      }
    };

    // Add connection state change handler
    const handleConnectionStateChange = () => {
      console.log("🔗 WebRTC connection state:", peerConnection.connectionState);
      if (peerConnection.connectionState === 'connected') {
        setIsConnecting(false);
      } else if (peerConnection.connectionState === 'failed' || peerConnection.connectionState === 'disconnected') {
        setIsConnecting(false);
        console.error("❌ WebRTC connection failed:", peerConnection.connectionState);
      }
    };

    // Add ICE connection state change handler
    const handleIceConnectionStateChange = () => {
      console.log("🧊 ICE connection state:", peerConnection.iceConnectionState);
    };

    // Add event listeners
    peerConnection.addEventListener('connectionstatechange', handleConnectionStateChange);
    peerConnection.addEventListener('iceconnectionstatechange', handleIceConnectionStateChange);

    console.log("🔌 Adding socket event listeners for call:", call._id);
    socket.on('call-offer', handleCallOffer);
    socket.on('call-answer', handleCallAnswer);
    socket.on('call-ice-candidate', handleIceCandidate);
    
    console.log("🔌 Socket event listeners added successfully");

    return () => {
      console.log("🔌 Cleaning up WebRTC event listeners for call:", call._id);
      peerConnection.removeEventListener('connectionstatechange', handleConnectionStateChange);
      peerConnection.removeEventListener('iceconnectionstatechange', handleIceConnectionStateChange);
      socket.off('call-offer', handleCallOffer);
      socket.off('call-answer', handleCallAnswer);
      socket.off('call-ice-candidate', handleIceCandidate);
      console.log("🔌 WebRTC event listeners cleaned up");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, peerConnection, call]);

  // Update video streams
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Call duration timer (only when call is active)
  useEffect(() => {
    if (!call || call.status !== 'active') return;
    
    const timer = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.status]);

  // Auto-hide controls
  useEffect(() => {
    const hideControls = () => {
      setShowControls(false);
    };

    const resetControlsTimer = () => {
      setShowControls(true);
      clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = setTimeout(hideControls, 3000);
    };

    resetControlsTimer();

    return () => {
      clearTimeout(controlsTimeoutRef.current);
    };
  }, []);

  // Validate props after all hooks
  if (!call || !currentUser) {
    console.error("❌ VideoCallWindow: Missing required props", { call: !!call, currentUser: !!currentUser });
    return null;
  }

  const initializeWebRTC = async () => {
    try {
      console.log("🚀 Initializing WebRTC for call:", call._id);
      console.log("🚀 Call data:", { 
        id: call._id, 
        type: call.type, 
        status: call.status,
        caller: call.caller,
        participants: call.participants
      });
      
      // Get user media with better constraints
      const stream = await navigator.mediaDevices.getUserMedia({
        video: call.type === 'video' ? {
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          frameRate: { ideal: 30, max: 60 }
        } : false,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      console.log("🎥 Got user media stream:", { 
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length
      });
      
      setLocalStream(stream);
      
      // Set local video
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        console.log("🎥 Set local video element");
      }

      // Create peer connection with better STUN configuration
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' }
        ],
        iceCandidatePoolSize: 10,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require'
      });

      console.log("🔗 Created RTCPeerConnection");
      setPeerConnection(pc);

      // Add local stream to peer connection
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      // Handle incoming streams
      pc.ontrack = (event) => {
        console.log("📹 Received remote stream:", event.streams[0]);
        setRemoteStream(event.streams[0]);
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
          console.log("📹 Set remote video element");
        }
        setIsConnecting(false);
      };

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          console.log("🧊 Sending ICE candidate:", event.candidate);
          const targetUserId = getOtherParticipantId();
          console.log("🧊 Target user ID:", targetUserId);
          
          socket.emit('call-ice-candidate', {
            candidate: event.candidate,
            callId: call._id,
            targetUserId: targetUserId
          });
        } else {
          console.log("🧊 ICE gathering completed");
        }
      };

      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        console.log("🔗 Connection state changed:", pc.connectionState);
        if (pc.connectionState === 'connected') {
          console.log("✅ WebRTC connection established");
          setIsConnecting(false);
        } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          console.error("❌ WebRTC connection failed:", pc.connectionState);
          setIsConnecting(false);
        }
      };

      // Handle ICE connection state changes
      pc.oniceconnectionstatechange = () => {
        console.log("🧊 ICE connection state changed:", pc.iceConnectionState);
        if (pc.iceConnectionState === 'connected') {
          console.log("✅ ICE connection established");
          setIsConnecting(false);
        } else if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
          console.error("❌ ICE connection failed:", pc.iceConnectionState);
          setIsConnecting(false);
        }
      };

      // If we're the caller, create and send offer
      if (call.caller._id === currentUser.id) {
        console.log("📞 Creating offer as caller");
        const targetUserId = getOtherParticipantId();
        console.log("📞 Target user ID:", targetUserId);
        
        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: call.type === 'video'
        });
        console.log("📞 Created offer:", offer);
        
        await pc.setLocalDescription(offer);
        console.log("📞 Set local description");
        
        socket.emit('call-offer', {
          offer: offer,
          callId: call._id,
          targetUserId: targetUserId
        });
        console.log("📞 Sent call offer to:", targetUserId);
      } else {
        console.log("📞 Not the caller, waiting for offer");
      }

    } catch (error) {
      console.error("❌ Error initializing WebRTC:", error);
      
      // Provide specific error messages
      if (error.name === 'NotAllowedError') {
        alert("Camera/microphone access denied. Please allow access and try again.");
      } else if (error.name === 'NotFoundError') {
        alert("No camera/microphone found. Please check your device.");
      } else if (error.name === 'NotReadableError') {
        alert("Camera/microphone is already in use by another application.");
      } else {
        alert("Failed to access camera/microphone. Please check permissions and try again.");
      }
    }
  };

  const getOtherParticipantId = () => {
    console.log("🔍 Getting other participant ID:", {
      callerId: call.caller._id,
      currentUserId: currentUser.id,
      participants: call.participants
    });
    
    if (call.caller._id === currentUser.id) {
      // We're the caller, get the first participant
      const participant = call.participants[0];
      const participantId = participant?.user?._id || participant?._id;
      console.log("🔍 Caller getting participant ID:", participantId);
      return participantId;
    } else {
      // We're the participant, get the caller
      console.log("🔍 Participant getting caller ID:", call.caller._id);
      return call.caller._id;
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const toggleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  // Get the other participant info
  const getOtherParticipant = () => {
    if (call.caller && call.caller._id !== currentUser.id) {
      return call.caller;
    }
    return call.participants?.find(p => p.user?._id !== currentUser.id)?.user || 
           call.participants?.find(p => p._id !== currentUser.id);
  };

  // Get call status text
  const getCallStatusText = () => {
    if (isConnecting) return 'Connecting...';
    switch (call.status) {
      case 'calling':
        return 'Calling...';
      case 'ringing':
        return 'Ringing...';
      case 'active':
        return 'Connected';
      default:
        return 'Connecting...';
    }
  };

  const otherParticipant = getOtherParticipant();

  // Debug render
  console.log("📞 VideoCallWindow rendering:", { 
    callId: call?._id, 
    callStatus: call?.status,
    hasLocalStream: !!localStream,
    hasRemoteStream: !!remoteStream,
    isConnecting
  });

  // Additional validation before render
  if (!call._id || !call.status) {
    console.error("❌ VideoCallWindow: Invalid call data", call);
    return null;
  }

  return (
    <div 
      className="video_call_window"
      onMouseMove={handleMouseMove}
    >
      {/* Remote Video (Main) */}
      <div className="remote_video_container">
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="remote_video"
        />
        
        {!remoteStream && (
          <div className="no_video_placeholder">
            <div className={`placeholder_avatar ${call.status === 'calling' ? 'calling' : ''}`}>
              <span className="placeholder_icon notranslate">👤</span>
            </div>
            {call.status === 'calling' && (
              <>
                <p>Calling...</p>
                <div className="calling_animation">
                  <div className="pulse"></div>
                  <div className="pulse"></div>
                  <div className="pulse"></div>
                </div>
              </>
            )}
            {call.status === 'ringing' && (
              <>
                <p>Ringing...</p>
                <div className="ringing_animation">📞</div>
              </>
            )}
            {call.status === 'active' && <p>Connecting...</p>}
          </div>
        )}
      </div>

      {/* Local Video (Picture-in-Picture) - only show for video calls */}
      {call.type === 'video' && (
        <div className="local_video_container">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="local_video"
          />
          
          {isVideoOff && (
            <div className="video_off_overlay">
              <span className="video_off_icon">📹</span>
            </div>
          )}
        </div>
      )}

      {/* Call Info */}
      {showControls && (
        <div className="call_info">
          <div className="participant_info">
            <h3 className="participant_name">
              {otherParticipant 
                ? `${otherParticipant.first_name} ${otherParticipant.last_name}` 
                : 'Unknown'
              }
            </h3>
            <span className={`call_status ${call.status}`}>
              {getCallStatusText()}
            </span>
          </div>
          {call.status === 'active' && (
            <div className="call_duration">
              {formatDuration(callDuration)}
            </div>
          )}
          {call.status === 'calling' && (
            <div className="call_type">
              {call.type === 'video' ? '📹' : '📞'} {call.type} call
            </div>
          )}
        </div>
      )}

      {/* Call Controls */}
      {showControls && (
        <div className="call_controls">
          <div className={`controls_wrapper ${(call.status === 'calling' || call.status === 'ringing') ? 'calling_state' : ''}`}>
            {/* During calling/ringing - show simplified controls */}
            {(call.status === 'calling' || call.status === 'ringing') && (
              <>
                {/* Cancel Call Button (prominent) */}
                <button
                  className="control_btn cancel_call_btn"
                  onClick={onEndCall}
                  title="Cancel call"
                >
                  <span className="control_icon">✕</span>
                </button>
              </>
            )}

            {/* During active call - show full controls */}
            {call.status === 'active' && (
              <>
                {/* Mute Button */}
                <button
                  className={`control_btn ${isMuted ? 'active' : ''}`}
                  onClick={toggleMute}
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  <span className="control_icon">
                    {isMuted ? '🔇' : '🎤'}
                  </span>
                </button>

                {/* Video Button */}
                <button
                  className={`control_btn ${isVideoOff ? 'active' : ''}`}
                  onClick={toggleVideo}
                  title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
                >
                  <span className="control_icon">
                    {isVideoOff ? '📹' : '📷'}
                  </span>
                </button>

                {/* End Call Button */}
                <button
                  className="control_btn end_call_btn"
                  onClick={onEndCall}
                  title="End call"
                >
                  <span className="control_icon">📞</span>
                </button>

                {/* Fullscreen Button */}
                <button
                  className="control_btn"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                >
                  <span className="control_icon">
                    {isFullscreen ? '⛶' : '⛶'}
                  </span>
                </button>

                {/* More Options */}
                <button
                  className="control_btn"
                  title="More options"
                >
                  <span className="control_icon">⋮</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Connection Status */}
      {!remoteStream && (
        <div className="connection_status">
          <div className="status_indicator">
            <div className="spinner"></div>
            <span>Connecting...</span>
          </div>
        </div>
      )}
    </div>
  );
}